/* eslint-disable no-underscore-dangle */
import axios from "axios";
import Keycloak from "keycloak-js";

class KeycloakAxios {
  constructor(configUrl) {
    this.kc = new Keycloak(configUrl);
    this.isLoggingOut = false; // Flag to prevent multiple logout attempts

    this.kc.onAuthLogout = () => {
      if (this.onAuthLogout) {
        setTimeout(this.onAuthLogout, 0);
      }
    };

    this.kc.onAuthError = (err) => {
      // eslint-disable-next-line no-console
      console.error("[Keycloak]: Authentication error", err);
    };

    this.kc.onAuthRefreshError = (err) => {
      // eslint-disable-next-line no-console
      console.error("[Keycloak]: Authentication refresh error", err);
    };
  }

  init = async (options) => {
    try {
      const authenticated = await this.kc.init(options);
      this._interceptAxiosRequest();

      // If not authenticated after check-sso and not on authorize route, redirect to login
      if (
        !authenticated &&
        !window.location.pathname.startsWith("/authorize")
      ) {
        console.warn(
          "[Keycloak]: Not authenticated after init, redirecting to login"
        );
        this.kc.login();
      }

      return authenticated;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Keycloak]: Initialization failed", err);
      throw err;
    }
  };

  _interceptAxiosRequest = () => {
    axios.interceptors.request.use(
      async (config) => {
        const isTokenValid = await this.validateToken();

        if (isTokenValid) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `Bearer ${this.kc.token}`;
          // eslint-disable-next-line no-param-reassign
          config.headers["Content-Type"] = `application/json`;
          return config;
        } else {
          // Token is invalid/expired - trigger logout and reject request
          console.warn(
            "[Keycloak]: Token validation failed, redirecting to login"
          );

          if (!this.isLoggingOut) {
            this.isLoggingOut = true;
            if (this.onAuthLogout) {
              setTimeout(this.onAuthLogout, 0);
            } else {
              this.logout();
            }
          }

          // Reject the request to prevent 404
          return Promise.reject(
            new Error("Session expired - redirecting to login")
          );
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle 401/403 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          console.warn(
            "[Keycloak]: Received 401/403 response, session may have expired"
          );

          if (!this.isLoggingOut) {
            this.isLoggingOut = true;
            // Trigger logout to clear session and redirect to login
            if (this.onAuthLogout) {
              setTimeout(this.onAuthLogout, 0);
            } else {
              this.logout();
            }
          }
        }
        return Promise.reject(error);
      }
    );
  };

  _getUserRoles = () => {
    const { tokenParsed } = this.kc;
    let roles = null;
    if (tokenParsed) {
      const entries = Object.entries(tokenParsed.resource_access);
      roles = entries.reduce((newObj, r) => {
        const name = r[0];
        const data = { ...newObj };
        data[name] = r[1].roles;
        return data;
      }, {});
    }
    return roles;
  };

  getUserData = () => {
    const { tokenParsed } = this.kc;

    return tokenParsed
      ? {
          username: tokenParsed.preferred_username,
          firstName: tokenParsed.given_name,
          lastName: tokenParsed.family_name,
          email: tokenParsed.email,
          roles: this._getUserRoles(),
        }
      : null;
  };

  validateToken = async () => {
    try {
      // If the token expires within 5 seconds (default value for updateToken, can pass eny value in seconds) the token is refreshed.
      await this.kc.updateToken();
      return true;
    } catch {
      return false;
    }
  };

  login = (options) => {
    this.kc.login(options);
  };

  logout = (options) => {
    this.kc.logout(options);
  };

  getDeprecatedAuthenticated = () => {
    // Deprecated
    console.warn(
      "[KeycloakContext]: context[isAuthenticated/authenticated] property is Deprecated, use context.keycloak.authenticated"
    );
    return this.kc?.authenticated;
  };
}

export const initKeycloak = async (configUrl, options) => {
  const keycloak = new KeycloakAxios(configUrl);
  await keycloak.init(options);
  return keycloak;
};
